import React, { useRef } from 'react';
import { useInvoiceStore } from '../stores/invoiceStore';
import { vatOptions } from '../constants/invoice';
import type { Settings } from '@dutchinvoice/types';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Globe2, ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useClickOutside } from '../hooks/useClickOutside';
import { LanguageNotice } from './LanguageNotice';
import { LANGUAGE_NAMES } from '../constants/languages';

const InvoiceSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const settings = useInvoiceStore((state) => state.settings);
  const updateSettings = useInvoiceStore((state) => state.updateSettings);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const handleSettingChange = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    updateSettings({ [key]: value });
  };



  return (
    <div className="relative mb-0 p-4 flex flex-col items-end" ref={containerRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative z-20 btn-secondary flex items-center gap-2 group"
        whileHover={{ y: -1 }}
        whileTap={{ y: 1 }}
      >
        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <SettingsIcon className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
        </motion.div>
        <span>{t('settings.title')}</span>
        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ 
              duration: 0.2, 
              ease: [0.32, 0.72, 0, 1]
            }}
            className="absolute top-[100%] right-4 z-30 w-80"
            style={{
              transformOrigin: 'top right',
            }}
          >
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 space-y-6">
              {/* Country Section (removed Language section) */}
              <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Globe2 className="w-4 h-4 text-gray-400" />
                    <span>{t('settings.country')}</span>
                  </div>
                </label>
                <select
                  value={settings.country}
                  onChange={(e) => handleSettingChange('country', e.target.value as Settings['country'])}
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm bg-white transition-colors"
                >
                  {vatOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.name} (VAT: {option.vat}%)
                    </option>
                  ))}
                </select>
              </motion.div>
              
              {/* Language Section */}
              <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Languages className="w-4 h-4 text-gray-400" />
                    <span>{t('settings.invoiceLanguage')}</span>
                  </div>
                </label>
                <select
                  value={settings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value as Settings['language'])}
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm bg-white transition-colors"
                >
                  {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
                <div className="mt-3">
                  <LanguageNotice />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceSettings; 